import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, History, Pencil, RotateCcw, Volume2 } from 'lucide-react';
import {
  approvalNotEligibleSchema,
  rowVersionConflictSchema,
  type Language,
  type Row,
} from '@tekvoice/shared';
import { BiText } from '@/components/bi-text';
import { AudioCell } from '@/components/review/audio-cell';
import { useReview } from '@/components/review/review-context';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/sonner';
import { ApiError, api } from '@/lib/api';
import { isApprovable, textFor } from '@/lib/assets';
import { cn } from '@/lib/utils';

export const REVIEW_GRID =
  'grid grid-cols-[36px_60px_minmax(180px,1fr)_minmax(180px,1fr)_180px_180px_150px] gap-2 px-2';

interface ConflictState {
  changedByName: string | null;
  currentVersion: number;
  row: Row;
}

interface ReviewRowProps {
  row: Row;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
  focused?: boolean;
}

export function ReviewRow({
  row,
  selected,
  onSelectedChange,
  focused = false,
}: ReviewRowProps): React.JSX.Element {
  const { applyRow, canEdit, canApprove, canUnapprove, openHistory, openOverride, overrideRowId } =
    useReview();
  const [editing, setEditing] = React.useState<Language | null>(null);
  const [draft, setDraft] = React.useState('');
  const [conflict, setConflict] = React.useState<ConflictState | null>(null);
  const overrideOpen = overrideRowId === row.id;

  const save = useMutation({
    mutationFn: (input: { language: Language; text: string }) =>
      api.rows.update(row.id, {
        version: row.version,
        ...(input.language === 'EN' ? { englishText: input.text } : { arabicText: input.text }),
      }),
    onSuccess: (result) => {
      applyRow(result.row);
      setEditing(null);
      setConflict(null);
      if (result.staleLanguages.length > 0) {
        toast.warning(
          `Saved — ${result.staleLanguages.join(' and ')} audio is now stale. Regenerate before approving.`,
        );
      } else {
        toast.success('Saved');
      }
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.status === 409) {
        const parsed = rowVersionConflictSchema.safeParse(error.body);
        if (parsed.success) {
          setConflict({
            changedByName: parsed.data.changedByName,
            currentVersion: parsed.data.currentVersion,
            row: parsed.data.row,
          });
          return;
        }
      }
      toast.error(error.message);
    },
  });

  const approve = useMutation({
    mutationFn: () => api.rows.approve(row.id, row.version),
    onSuccess: (result) => {
      applyRow(result.row);
      toast.success(`Row ${row.rowIndex + 1} approved — published as ${result.publishedSlug}`);
    },
    onError: (error: Error) => {
      if (error instanceof ApiError) {
        const notEligible = approvalNotEligibleSchema.safeParse(error.body);
        if (notEligible.success) {
          toast.error(
            `Cannot approve row ${row.rowIndex + 1}: ${notEligible.data.reasons
              .map((reason) => `${reason.language ?? 'row'} ${reason.reason.replace(/_/g, ' ')}`)
              .join(', ')}`,
          );
          return;
        }
      }
      toast.error(error.message);
    },
  });

  const unapprove = useMutation({
    mutationFn: () => api.rows.unapprove(row.id),
    onSuccess: (result) => {
      applyRow(result.row);
      toast.success(`Row ${row.rowIndex + 1} un-approved — published files removed`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const startEdit = (language: Language): void => {
    setEditing(language);
    setDraft(textFor(row, language));
  };

  const approvable = isApprovable(row);

  const textCell = (language: Language): React.JSX.Element => {
    const value = textFor(row, language);
    const arabic = language === 'AR';
    if (editing === language) {
      return (
        <div className="space-y-2 py-1">
          <Textarea
            autoFocus
            value={draft}
            aria-label={`${language} text for row ${row.rowIndex + 1}`}
            dir="auto"
            {...(arabic ? { lang: 'ar' } : {})}
            className={cn('min-h-16 text-sm', arabic && 'font-arabic leading-loose')}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setEditing(null);
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                save.mutate({ language, text: draft });
              }
            }}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={save.isPending}
              onClick={() => save.mutate({ language, text: draft })}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" disabled={save.isPending} onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="group flex items-start gap-1 py-1">
        {value.length > 0 ? (
          <BiText as="div" className={cn('flex-1 text-sm', arabic && 'text-base')}>
            {value}
          </BiText>
        ) : (
          <span className="flex-1 text-xs italic text-muted-foreground">blank</span>
        )}
        {canEdit ? (
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
            aria-label={`Edit ${language} text for row ${row.rowIndex + 1}`}
            onClick={() => startEdit(language)}
          >
            <Pencil className="h-3 w-3" />
          </Button>
        ) : null}
      </div>
    );
  };

  return (
    <div
      data-testid={`review-row-${row.rowIndex}`}
      data-focused={focused || undefined}
      className={cn(
        'border-b last:border-b-0',
        selected && 'bg-muted/40',
        focused && 'ring-1 ring-inset ring-primary/40',
      )}
    >
      <div role="row" className={cn(REVIEW_GRID, 'items-start py-2')}>
        <div role="cell" className="pt-2">
          <Checkbox
            checked={selected}
            aria-label={`Select row ${row.rowIndex + 1}`}
            onCheckedChange={(value) => onSelectedChange(value === true)}
          />
        </div>

        <div role="cell" className="pt-1.5">
          <div className="text-sm tabular-nums text-muted-foreground">{row.rowIndex + 1}</div>
          {row.publishedSlug ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block max-w-[52px] truncate text-[10px] text-muted-foreground">
                  {row.publishedSlug}
                </span>
              </TooltipTrigger>
              <TooltipContent>Published slug (frozen): {row.publishedSlug}</TooltipContent>
            </Tooltip>
          ) : null}
        </div>

        <div role="cell">{textCell('EN')}</div>
        <div role="cell">{textCell('AR')}</div>

        <div role="cell" className="pt-1">
          <AudioCell row={row} language="EN" />
        </div>
        <div role="cell" className="pt-1">
          <AudioCell row={row} language="AR" />
        </div>

        <div role="cell" className="flex flex-wrap items-start gap-1 pt-1">
          {canApprove ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant={row.approvedAt ? 'secondary' : 'default'}
                    className="h-7"
                    disabled={!approvable || approve.isPending}
                    aria-label={`Approve row ${row.rowIndex + 1}`}
                    onClick={() => approve.mutate()}
                  >
                    <Check className="h-3.5 w-3.5" />
                    {row.approvedAt ? 'Re-approve' : 'Approve'}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {approvable
                  ? 'Publish both languages to Google Cloud Storage'
                  : 'Both languages must be generated, non-blank and not stale'}
              </TooltipContent>
            </Tooltip>
          ) : null}

          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            aria-label={`History for row ${row.rowIndex + 1}`}
            onClick={() => openHistory(row)}
          >
            <History className="h-3.5 w-3.5" />
          </Button>

          {canEdit ? (
            <Button
              size="icon"
              variant="ghost"
              className={cn('h-7 w-7', overrideOpen && 'bg-accent')}
              aria-label={`Fix pronunciation for row ${row.rowIndex + 1}`}
              aria-expanded={overrideOpen}
              onClick={() => openOverride(overrideOpen ? null : row.id)}
            >
              <Volume2 className="h-3.5 w-3.5" />
            </Button>
          ) : null}

          {canUnapprove && row.approvedAt ? (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              aria-label={`Un-approve row ${row.rowIndex + 1}`}
              disabled={unapprove.isPending}
              onClick={() => unapprove.mutate()}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </div>

      {conflict ? (
        <div
          role="alert"
          className="mx-2 mb-2 flex flex-wrap items-center gap-2 rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          <span>
            Changed by {conflict.changedByName ?? 'someone else'} — reload row to see their version
            before saving again.
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              applyRow(conflict.row);
              setConflict(null);
              setEditing(null);
              toast.info('Row reloaded');
            }}
          >
            Reload row
          </Button>
        </div>
      ) : null}

      {overrideOpen ? <OverridePanel row={row} /> : null}
    </div>
  );
}

/** SPEC §11 — the collapsible "Fix pronunciation" panel. */
function OverridePanel({ row }: { row: Row }): React.JSX.Element {
  const { applyRow, openOverride } = useReview();
  const [en, setEn] = React.useState(row.spokenOverrideEn ?? '');
  const [ar, setAr] = React.useState(row.spokenOverrideAr ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.rows.updateOverride(row.id, {
        version: row.version,
        spokenOverrideEn: en.trim().length > 0 ? en : null,
        spokenOverrideAr: ar.trim().length > 0 ? ar : null,
      }),
    onSuccess: (result) => {
      applyRow(result.row);
      toast.success(
        result.staleLanguages.length > 0
          ? `Override saved — ${result.staleLanguages.join(' and ')} audio is now stale`
          : 'Override saved',
      );
      openOverride(null);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="mx-2 mb-3 rounded-md border bg-muted/30 p-3">
      <p className="text-sm font-medium">Fix pronunciation</p>
      <p className="mb-3 text-xs text-muted-foreground">
        What is sent to the voice model instead of the text above. The export always keeps the
        original text.
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor={`override-en-${row.id}`}>
            English spoken override
          </label>
          <Textarea
            id={`override-en-${row.id}`}
            value={en}
            dir="auto"
            className="min-h-16 text-sm"
            placeholder={row.englishText}
            onChange={(event) => setEn(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor={`override-ar-${row.id}`}>
            Arabic spoken override
          </label>
          <Textarea
            id={`override-ar-${row.id}`}
            value={ar}
            dir="auto"
            lang="ar"
            className="min-h-16 font-arabic text-base leading-loose"
            placeholder={row.arabicText}
            onChange={(event) => setAr(event.target.value)}
          />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save override'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => openOverride(null)}>
          Close
        </Button>
      </div>
    </div>
  );
}
